'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useRouter } from 'next/navigation';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { Field } from '../../components/auth/field';
import { contactUs } from '@eliadmin/core/src/api/jobportal-api';
import { useWatch } from 'react-hook-form';

const customOfferSchema = z.object({
  name: z.string().min(1, 'Meno je povinné'),
  email: z.string().email('Neplatný email'),
  customerType: z.enum(['agentura', 'firma']),
  offerQty: z.string().min(1, 'Zadajte počet ponúk'),
  toppingQty: z.string().min(1, 'Zadajte počet zvýraznení'),
  curriculums: z.string().min(1, 'Zadajte počet životopisov'),
  note: z.string().optional(),
  unlockOffersQty: z.string().optional(),
  highlightOffersQty: z.string().optional(),
  curriculumsQty: z.string().optional(),
});

type CustomOfferForm = z.infer<typeof customOfferSchema>;

export default function CustomOfferPage() {
  const { me } = useCustomerStore();
  const router = useRouter();
  const { toast } = useToast();

  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CustomOfferForm>({
    resolver: zodResolver(customOfferSchema),
    defaultValues: {
      name: me?.customer?.name || '',
      email: me?.customer?.email || '',
      customerType: 'firma',
      offerQty: '0',
      toppingQty: '0',
      curriculums: '0',
      note: '',
      unlockOffersQty: '0',
      highlightOffersQty: '0',
      curriculumsQty: '0',
    },
  });

  const customerType = useWatch({ control: form.control, name: 'customerType' });

  const onSubmit = form.handleSubmit(async data => {
    setSubmitting(true);
    try {
      // Prepare payload for contactUs
      const payload = {
        subject: `Individuálna ponuka - ${
          data.customerType === 'agentura' ? 'Agentúra' : 'Firma'
        } (${data.name})`,
        email: data.email,
        text: `Meno: ${data.name}\nTyp: ${
          data.customerType === 'agentura' ? 'Agentúra' : 'Firma'
        }\n${customerType === 'agentura' ? 'Počet kurzov' : 'Počet ponúk'}: ${data.offerQty}\n${
          customerType === 'agentura' ? 'Počet zvýraznení kurzov' : 'Počet zvýraznení'
        }: ${data.toppingQty}${
          customerType === 'firma' ? `\nPočet životopisov na odomknutie: ${data.curriculums}` : ''
        }\nPoznámka: ${data.note || '-'}`,
        name: data.name,
        surname: '',
        order_number: '',
        phone: '',
        attachments: [],
        custom: {
          'Typ zákazníka': data.customerType === 'agentura' ? 'Agentúra' : 'Firma',
          [customerType === 'agentura' ? 'Počet kurzov' : 'Počet ponúk']: data.offerQty,
          [customerType === 'agentura' ? 'Počet zvýraznení kurzov' : 'Počet zvýraznení']:
            data.toppingQty,
          ...(customerType === 'firma'
            ? { 'Počet životopisov na odomknutie': data.curriculums }
            : {}),
          Správa: data.note,
        },
        gdpr: true,
        newsletter: false,
        newsletter_return_url: '',
        'g-recaptcha-response': '',
      };
      await contactUs(payload);
      toast({ title: 'Žiadosť bola odoslaná', variant: 'success' });
      router.push('/');
    } catch (err) {
      toast({ title: 'Chyba pri odosielaní', description: String(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="mx-auto max-w-xl px-4 py-10">
      <h1 className="mb-6 text-center text-2xl font-bold">Individuálna ponuka</h1>
      <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            control={form.control}
            name="customerType"
            type="radio"
            options={[
              { value: 'firma', label: 'Firma' },
              { value: 'agentura', label: 'Agentúra' },
            ]}
            className={{
              wrapper: 'flex justify-center',
            }}
          />
          <Field
            control={form.control}
            name="name"
            label={`${
              customerType === 'firma' ? 'Názov firmy' : 'Názov agentúry'
            } / Meno a priezvisko`}
            required
          />
          <Field control={form.control} name="email" label="Email" type="email" required />

          <div className="font-semibold">
            {customerType === 'agentura' ? 'Počet kurzov' : 'Počet ponúk'}
          </div>
          <Field control={form.control} name="offerQty" type="number" required />
          <div className="font-semibold">
            {customerType === 'agentura' ? 'Počet zvýraznení kurzov' : 'Počet zvýraznení'}
          </div>
          <Field control={form.control} name="toppingQty" type="number" required />
          {customerType === 'firma' && (
            <>
              <div className="font-semibold">Počet životopisov na odomknutie</div>
              <Field control={form.control} name="curriculums" type="number" required />
            </>
          )}
          <Field control={form.control} name="note" label="Správa" type="textarea" />
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? 'Odosielam...' : 'Odoslať záujem o individuálnu ponuku'}
          </Button>
        </form>
      </Form>
      <div className="mt-6 flex justify-center">
        <Button variant="link" onClick={() => router.push('/kontakt')}>
          Kontaktujte nás
        </Button>
      </div>
    </div>
  );
}
